import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, RefreshCw } from "lucide-react";
import { MaskedLine, EASE } from "@/components/Reveal";

const FLOW = [
  { code: "01", label: "DISCOVER", note: "Understand before you invest" },
  { code: "02", label: "DEFINE", note: "Specify what must change" },
  { code: "03", label: "VALIDATE", note: "Prototype before you build" },
  { code: "04", label: "DELIVER", note: "Govern the implementation" },
  { code: "05", label: "MEASURE", note: "Prove the outcome" },
];

const HeroFlow = () => (
  <div className="relative pr-0 md:pr-16" data-testid="hero-flow-diagram">
    <p className="mb-5 font-mono text-[10px] tracking-[0.3em] text-slate-400">
      [FIG.01] — THE SERVICE MODEL
    </p>
    {FLOW.map((step, i) => (
      <motion.div
        key={step.label}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.95 + i * 0.12, duration: 0.7, ease: EASE }}
        data-testid={`hero-flow-step-${step.label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <div className="flex items-center gap-4 border border-line bg-white px-5 py-4">
          <span className="w-7 font-mono text-[10px] text-slate-400">[{step.code}]</span>
          <span className="font-mono text-xs tracking-[0.18em] text-ink">{step.label}</span>
          <span className="ml-auto hidden text-xs text-slate-500 sm:block">{step.note}</span>
        </div>
        {i < FLOW.length - 1 && (
          <div className="flex justify-start py-1 pl-8" aria-hidden="true">
            <ArrowDown className="h-3.5 w-3.5 text-slate-400" />
          </div>
        )}
      </motion.div>
    ))}

    <svg
      className="absolute right-0 top-10 hidden h-[calc(100%-3.5rem)] w-14 md:block"
      viewBox="0 0 56 520"
      fill="none"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <marker
          id="fbArrow"
          viewBox="0 0 8 8"
          refX="6"
          refY="4"
          markerWidth="6"
          markerHeight="6"
          orient="auto"
        >
          <path d="M0 0 L8 4 L0 8 z" fill="#2563EB" />
        </marker>
      </defs>
      <path
        d="M 6 508 C 52 508 52 12 8 12"
        stroke="#2563EB"
        strokeWidth="1"
        strokeDasharray="4 6"
        className="animate-dash"
        markerEnd="url(#fbArrow)"
      />
    </svg>

    <motion.p
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.7, duration: 0.8 }}
      className="mt-4 flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-slate-400"
    >
      <RefreshCw className="h-3 w-3 text-accent" aria-hidden="true" />
      CONTINUOUS FEEDBACK LOOP
    </motion.p>
  </div>
);

export default function HeroSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0.3]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden border-b border-line"
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(15,23,42,0.045) 1px, transparent 1px)",
          backgroundSize: "25% 100%",
        }}
        aria-hidden="true"
      />
      <div className="relative mx-auto grid max-w-7xl gap-16 px-6 pb-20 pt-32 sm:pt-40 lg:grid-cols-12 lg:pb-28">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mb-8 flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] text-slate-500"
          >
            <span className="h-px w-10 bg-slate-300" aria-hidden="true" />
            PRODUCT & TRANSFORMATION ADVISORY
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-serif text-4xl leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-6xl"
          >
            <MaskedLine delay={0.25}>Understand what needs</MaskedLine>
            <MaskedLine delay={0.37}>to change — before you</MaskedLine>
            <MaskedLine delay={0.49}>invest in changing it.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.9, ease: EASE }}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            We lead product discovery, requirements and workflow analysis, rapid
            prototyping, vendor and solution evaluation, and implementation governance —
            so modernization efforts solve the right problems and deliver measurable
            outcomes.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.9, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-discuss"
              className="group inline-flex items-center gap-2 bg-navy px-7 py-4 text-sm tracking-wide text-white transition-colors duration-300 hover:bg-accent-deep"
            >
              Discuss a System
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/approach"
              data-testid="hero-cta-approach"
              className="inline-flex items-center gap-2 border border-slate-300 px-7 py-4 text-sm tracking-wide text-ink transition-colors duration-300 hover:border-ink"
            >
              Our Approach
            </Link>
          </motion.div>
        </div>

        <motion.div style={{ y, opacity }} className="lg:col-span-5">
          <HeroFlow />
        </motion.div>
      </div>
    </section>
  );
}
